import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
} from "react-bootstrap";
import {
  FaRocket,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaDownload,
  FaBrain,
} from "react-icons/fa";
import Layout from "./Layout";
import axios from "axios";
import "../assets/css/SpaSingleKeyword.css";

const SpKeywordGroup = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  // File state
  const [keywordsFile, setKeywordsFile] = useState(null);
  const [asinsFile, setAsinsFile] = useState(null);
  const [negativeFile, setNegativeFile] = useState(null);

  // Grouping query state
  const [groupingQuery, setGroupingQuery] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [generationDiagnostics, setGenerationDiagnostics] = useState(null);
  const [creationDiagnostics, setCreationDiagnostics] = useState(null);
  const [creationComplete, setCreationComplete] = useState(false);
  const [campaignState, setCampaignState] = useState("PAUSED");

  // Bulk actions states
  const [bulkAction, setBulkAction] = useState("");
  const [bulkBudget, setBulkBudget] = useState("");
  const [bulkStartDate, setBulkStartDate] = useState("");
  const [bulkEndDate, setBulkEndDate] = useState("");

  // Handle file uploads
  const handleKeywordsFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const firstLine = text.split("\n")[0].toLowerCase();
        if (!firstLine.includes("keyword")) {
          alert("Advertencia: El archivo de Keywords no parece tener una columna 'Keyword'. La generación podría fallar si falta este campo obligatorio.");
        }
      };
      reader.readAsText(file);
      setKeywordsFile(file);
    }
  };

  const handleAsinsFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const firstLine = text.split("\n")[0].toLowerCase();
        if (!firstLine.includes("sku")) {
          alert("Advertencia: El archivo de ASINs no parece tener una columna 'SKU'. Para cuentas tipo Seller, Amazon requiere el SKU para enlazar el producto correctamente. Sin él, la campaña se creará pero el producto no se añadirá.");
        }
      };
      reader.readAsText(file);
      setAsinsFile(file);
    }
  };

  const handleNegativeFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setNegativeFile(file);
    }
  };

  // Generate campaigns with AI grouping
  const handleGenerateCampaigns = async () => {
    if (!keywordsFile || !asinsFile) {
      setError("You must upload the Keywords and ASINs files");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setAnalysis(null);
    setGenerationDiagnostics(null);
    setCreationDiagnostics(null);

    try {
      const formData = new FormData();
      formData.append("keywords", keywordsFile);
      formData.append("asins", asinsFile);
      formData.append("groupingQuery", groupingQuery);

      const response = await axios.post(
        `${API_URL}/keyword-grouping/generate`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        const generatedCampaigns = response.data.campaigns || [];

        // Add 'selected' field to each campaign
        const campaignsWithSelection = generatedCampaigns.map((campaign) => ({
          ...campaign,
          selected: true,
        }));

        setCampaigns(campaignsWithSelection);
        setAnalysis({
          grouping_analysis: response.data.grouping_analysis,
          groups_summary: response.data.groups_summary,
          total_groups: response.data.total_groups,
        });
        setGenerationDiagnostics({
          requestId: response.data?.requestId || null,
          totalCampaigns: generatedCampaigns.length,
          totalGroups: response.data?.total_groups || 0,
          groupingApplied: !!groupingQuery?.trim(),
          debugTrace: response.data?.debugTrace || [],
        });

        setResult({
          success: true,
          message: `${generatedCampaigns.length} campaigns generated in ${response.data.total_groups} group(s). Select which campaigns to create in Amazon.`,
          count: generatedCampaigns.length,
        });
      } else {
        setGenerationDiagnostics({
          requestId: response.data?.requestId || null,
          totalCampaigns: 0,
          totalGroups: 0,
          groupingApplied: !!groupingQuery?.trim(),
          debugTrace: response.data?.debugTrace || [],
        });
        setError(response.data.error || "Error generating campaigns");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Error generating campaigns",
      );
      setGenerationDiagnostics({
        requestId: err.response?.data?.requestId || null,
        totalCampaigns: 0,
        totalGroups: 0,
        groupingApplied: !!groupingQuery?.trim(),
        debugTrace: err.response?.data?.debugTrace || [],
      });
    } finally {
      setLoading(false);
    }
  };

  // Create campaigns in Amazon
  const handleCreateInAmazon = async () => {
    const selectedIndices = (campaigns || []).map((c, idx) => (c.selected ? idx : null))
      .filter((idx) => idx !== null);

    if (selectedIndices.length === 0) {
      setError("You must select at least one campaign to create in Amazon");
      return;
    }

    if (!keywordsFile || !asinsFile) {
      setError("The original Keywords and ASINs files are required");
      return;
    }

    setLoading(true);
    setError(null);
    setCreationDiagnostics(null);

    try {
      const profileId = localStorage.getItem("selectedAccountValue");

      if (!profileId) {
        setError(
          "Please select an account in the selector above before creating campaigns",
        );
        setLoading(false);
        return;
      }

      // Send FormData with files and selected campaign indices
      const formData = new FormData();
      formData.append("keyword_file", keywordsFile);
      formData.append("asin_file", asinsFile);
      if (negativeFile) {
        formData.append("negative_file", negativeFile);
      }
      formData.append("profileId", profileId);
      formData.append("campaignState", campaignState);
      formData.append(
        "selected_campaign_indices",
        JSON.stringify(selectedIndices),
      );
      formData.append("groupingQuery", groupingQuery);

      const response = await axios.post(
        `${API_URL}/spa-campaigns/create-in-amazon`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const creationErrors = response.data.errors || [];
      const debugTrace = response.data.debugTrace || [];
      const failureReasons = getFailureReasons(creationErrors, debugTrace);
      const campaignsCreated = response.data?.campaigns_created || 0;
      const campaignsFailed = response.data?.campaigns_failed || 0;
      setCreationDiagnostics({
        requestId: response.data?.requestId || null,
        campaignsCreated,
        campaignsFailed,
        errors: creationErrors,
        debugTrace,
        failureReasons,
        success: !!response.data.success,
      });

      setResult({
        success: !!response.data.success,
        message: response.data.success
          ? `Created ${campaignsCreated} campaigns in Amazon. ${campaignsFailed} errors.`
          : `Campaign creation finished with issues. Created ${campaignsCreated}, failed ${campaignsFailed}.`,
        count: campaignsCreated,
        amazonResults: response.data.results || [],
        errors: creationErrors,
      });

      if (response.data.success) {
        setCreationComplete(true);
      } else {
        const firstReason = failureReasons?.[0];
        setError(
          firstReason ||
            response.data.error ||
            "Error creating campaigns in Amazon",
        );
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Error creating campaigns in Amazon",
      );
      setCreationDiagnostics({
        requestId: err.response?.data?.requestId || null,
        campaignsCreated: err.response?.data?.campaigns_created || 0,
        campaignsFailed: err.response?.data?.campaigns_failed || 0,
        errors: err.response?.data?.errors || [],
        debugTrace: err.response?.data?.debugTrace || [],
        failureReasons: getFailureReasons(
          err.response?.data?.errors || [],
          err.response?.data?.debugTrace || [],
        ),
        success: false,
      });
    } finally {
      setLoading(false);
    }
  };

  // Toggle selection
  const toggleCampaignSelection = (index) => {
    const updatedCampaigns = [...campaigns];
    updatedCampaigns[index].selected = !updatedCampaigns[index].selected;
    setCampaigns(updatedCampaigns);
  };

  const selectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: true }));
    setCampaigns(updatedCampaigns);
  };

  const deselectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: false }));
    setCampaigns(updatedCampaigns);
  };

  // Apply bulk action
  const applyBulkAction = () => {
    if (!bulkAction) {
      setError("Select a bulk action");
      return;
    }

    const selectedCount = campaigns.filter((c) => c.selected).length;
    if (selectedCount === 0) {
      setError("Select at least one campaign");
      return;
    }

    const updatedCampaigns = campaigns.map((campaign) => {
      if (!campaign.selected) return campaign;

      const updated = { ...campaign };

      switch (bulkAction) {
        case "budget":
          if (bulkBudget && parseFloat(bulkBudget) > 0) {
            updated.Budget = parseFloat(bulkBudget);
          }
          break;
        case "startDate":
          if (bulkStartDate) {
            updated["Campaign Start Date"] = bulkStartDate;
            updated["Start Date"] = bulkStartDate;
          }
          break;
        case "endDate":
          if (bulkEndDate) {
            updated["Campaign End Date"] = bulkEndDate;
            updated["End Date"] = bulkEndDate;
          }
          break;
        default:
          break;
      }

      return updated;
    });

    setCampaigns(updatedCampaigns);
    setBulkAction("");
    setBulkBudget("");
    setBulkStartDate("");
    setBulkEndDate("");
    setError(null);
    setResult({
      success: true,
      message: `Action applied to ${selectedCount} campaign(s)`,
      count: selectedCount,
    });
  };

  const formatDateCell = (value) => {
    if (!value) return "-";
    const asString = String(value);
    if (asString.includes("T")) {
      return asString.slice(0, 10);
    }
    return asString;
  };

  const getFailureReasons = (errors = [], trace = []) => {
    const fromErrors = (errors || [])
      .flatMap((entry) => entry?.errors || [])
      .filter(Boolean);

    const fromTrace = (trace || [])
      .filter((item) => !item?.success && Array.isArray(item?.errors))
      .flatMap((item) => item.errors)
      .filter(Boolean);

    return Array.from(new Set([...fromErrors, ...fromTrace]));
  };

  // Download CSV
  const handleDownloadCampaigns = () => {
    if (campaigns.length === 0) return;

    const headers = Object.keys(campaigns[0]);
    const csvContent = [
      headers.join(","),
      ...campaigns.map((campaign) =>
        headers
          .map((header) => {
            const value = campaign[header];
            if (typeof value === "string" && value.includes(",")) {
              return `"${value}"`;
            }
            return value;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `sp_keyword_group_campaigns_${Date.now()}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset
  const handleReset = () => {
    setKeywordsFile(null);
    setAsinsFile(null);
    setNegativeFile(null);
    setGroupingQuery("");
    setCampaigns([]);
    setResult(null);
    setError(null);
    setAnalysis(null);
    setGenerationDiagnostics(null);
    setCreationDiagnostics(null);
    setCreationComplete(false);
  };

  return (
    <Layout>
      <Container fluid className="spa-keyword-campaigns-page py-4">
        <Row className="mb-4">
          <Col>
            <h2 className=" mb-3">
              <FaBrain className="me-2" />
              SP Keyword Campaigns - AI Grouping
            </h2>
            <p className="text-muted">
              Generate SP campaigns with intelligent keyword grouping by
              Search Volume
            </p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
        )}

        {generationDiagnostics && (
          <Alert variant="secondary" className="mb-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <strong>Generation diagnostics</strong>
              <small>Request ID: {generationDiagnostics.requestId || "n/a"}</small>
            </div>
            <div className="mb-0">
              Campaigns: {generationDiagnostics.totalCampaigns} | Groups: {generationDiagnostics.totalGroups} | Grouping applied: {generationDiagnostics.groupingApplied ? "Yes" : "No"}
            </div>
            {generationDiagnostics.debugTrace?.length > 0 && (
              <div className="small mt-2">
                {generationDiagnostics.debugTrace.map((step, idx) => (
                  <div key={`gen-trace-${idx}`}>
                    {idx + 1}. {step.step || "step"}
                    {step.reason ? ` - ${step.reason}` : ""}
                  </div>
                ))}
              </div>
            )}
          </Alert>
        )}

        {creationDiagnostics && (
          <Alert
            variant={creationDiagnostics.campaignsFailed > 0 ? "warning" : "success"}
            className="mb-4"
          >
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <strong>Amazon creation diagnostics</strong>
              <small>Request ID: {creationDiagnostics.requestId || "n/a"}</small>
            </div>
            <div className="mb-2">
              Created: {creationDiagnostics.campaignsCreated} | Failed: {creationDiagnostics.campaignsFailed}
            </div>
            {creationDiagnostics.failureReasons?.length > 0 && (
              <div className="mb-2 small">
                <strong>Failure reasons:</strong>
                <ul className="mb-0 mt-1 ps-3">
                  {creationDiagnostics.failureReasons.map((reason, idx) => (
                    <li key={`reason-${idx}`}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}
            {creationDiagnostics.debugTrace?.length > 0 && (
              <div className="small">
                {creationDiagnostics.debugTrace.map((item, idx) => (
                  <div key={`${item.campaignName || "campaign"}-${idx}`} className="mb-2 p-2 border rounded">
                    <div>
                      <strong>{item.campaignName || `Campaign ${idx + 1}`}</strong>
                      <Badge bg={item.success ? "success" : "danger"} className="ms-2">
                        {item.success ? "Success" : "Failed"}
                      </Badge>
                    </div>
                    {item.success && (
                      <div className="mt-1">
                        Campaign ID: {item.campaignId || "n/a"} | Ad Group ID: {item.adGroupId || "n/a"} | Product Ads: {item.productAdsCount || 0}
                      </div>
                    )}
                    {!item.success && Array.isArray(item.errors) && item.errors.length > 0 && (
                      <ul className="mb-0 mt-1 ps-3">
                        {item.errors.map((detail, detailIdx) => (
                          <li key={`${idx}-${detailIdx}`}>{detail}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Alert>
        )}

        {/* Upload Section */}
        {!result && (
          <>
            {/* AI Grouping Query */}
            <div className="config-section-card mb-4">
              <h5 className="mb-3">
                <FaBrain className="me-2 text-primary" />
                AI Grouping (Optional)
              </h5>
              <Form.Control
                type="text" placeholder="Example: search volume greater than 30k, sv between 10k and 50k, keywords with sv below 5000..."
                value={groupingQuery}
                onChange={(e) => setGroupingQuery(e.target.value)}
                className="custom-input"
              />
              <small className="text-muted mt-2 d-block">
                Describe how you want to group keywords. Use natural language
                like "search volume greater than 30k" or "sv between 10k and 50k".
              </small>
            </div>

            {/* Upload Grid */}
            <div className="upload-grid">
              {/* Card 1: Keywords */}
              <div className={`upload-card-new ${keywordsFile ? "has-file" : ""}`}
              >
                <div className="card-required-badge">Required</div>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">Keywords CSV</div>
                <div className="upload-card-desc">
                  Columns: Keyword, SV (Search Volume), Sugg Bid, Match Type
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {keywordsFile ? "Change File" : "Select File"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleKeywordsFileChange}
                    hidden
                  />
                </label>
                {keywordsFile && (
                  <small className="text-success mt-2">
                    {keywordsFile.name}
                  </small>
                )}
              </div>

              {/* Card 2: ASINs */}
              <div className={`upload-card-new ${asinsFile ? "has-file" : ""}`}>
                <div className="card-required-badge">Required</div>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">ASINs CSV</div>
                <div className="upload-card-desc">
                  Columns: ASIN, SKU, Abbreviation
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {asinsFile ? "Change File" : "Select File"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleAsinsFileChange}
                    hidden
                  />
                </label>
                {asinsFile && (
                  <small className="text-success mt-2">{asinsFile.name}</small>
                )}
              </div>

              {/* Card 3: Negative (Optional) */}
              <div className={`upload-card-new ${negativeFile ? "has-file" : ""}`}
              >
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">
                  Negative Targeting (Optional)
                </div>
                <div className="upload-card-desc">
                  Columns: Abbreviation, Negative Targeting, Match Type
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {negativeFile ? "Change File" : "Select File"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleNegativeFileChange}
                    hidden
                  />
                </label>
                {negativeFile && (
                  <small className="text-success mt-2">
                    {negativeFile.name}
                  </small>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <div className="generate-section">
              <button className={`btn-generate-campaigns ${!loading && keywordsFile && asinsFile ? "ready" : ""}`}
                onClick={handleGenerateCampaigns}
                disabled={loading || !keywordsFile || !asinsFile}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Generating campaigns with AI...
                  </>
                ) : (
                  <>
                    <FaBrain className="me-2" />
                    Generate Campaigns with AI Grouping
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Analysis Section */}
        {analysis && (
          <Alert variant="info" className="mb-3">
            <strong>🧠 AI Analysis:</strong> {analysis.grouping_analysis} |
            <strong> Groups:</strong> {analysis.total_groups} |
            <strong> Summary:</strong> {analysis.groups_summary}
          </Alert>
        )}

        {/* Results Section */}
        {result && campaigns.length > 0 && (
          <Row>
            <Col>
              <Card className="config-section-card border-0 p-0 overflow-hidden">
                <Card.Header className="  p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>
                      <FaBrain className="me-2" />
                      Generated Campaigns ({campaigns.length})
                    </h5>
                  </div>

                  <div>
                    {/* Bulk Actions Row */}
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <div className="d-flex gap-2 align-items-center">
                        <span className=" fw-bold">
                          Bulk actions:
                        </span>
                        <Form.Select
                          size="sm"
                          value={bulkAction}
                          onChange={(e) => setBulkAction(e.target.value)}
                          style={{ width: "200px" }}
                        >
                          <option value="">Select action</option>
                          <option value="budget">Set budget...</option>
                          <option value="startDate">Set Start Date...</option>
                          <option value="endDate">Set End Date...</option>
                        </Form.Select>

                        {bulkAction === "budget" && (
                          <Form.Control
                            type="number"
                            size="sm"
                            placeholder="New budget"
                            value={bulkBudget}
                            onChange={(e) => setBulkBudget(e.target.value)}
                            style={{ width: "150px" }}
                            min="0"
                            step="0.01"
                          />
                        )}

                        {bulkAction === "startDate" && (
                          <Form.Control
                            type="date"
                            size="sm"
                            value={bulkStartDate}
                            onChange={(e) => setBulkStartDate(e.target.value)}
                            style={{ width: "150px" }}
                          />
                        )}

                        {bulkAction === "endDate" && (
                          <Form.Control
                            type="date"
                            size="sm"
                            value={bulkEndDate}
                            onChange={(e) => setBulkEndDate(e.target.value)}
                            style={{ width: "150px" }}
                          />
                        )}

                        {bulkAction && (
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={applyBulkAction}
                          >
                            Apply
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons Row */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Select
                          size="sm"
                          value={campaignState}
                          onChange={(e) => setCampaignState(e.target.value)}
                          style={{ width: "auto" }}
                          disabled={creationComplete}
                        >
                          <option value="ENABLED">Create as: Enabled</option>
                          <option value="PAUSED">Create as: Paused</option>
                        </Form.Select>

                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={handleDownloadCampaigns}
                        >
                          <FaDownload className="me-1" /> Download CSV
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleReset}
                        >
                          <FaRocket className="me-1" /> New Generation
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  className="p-0 table-responsive " style={{ maxHeight : "600px", overflowY: "auto" }}
                >
                  <Table
                    hover
                    
                    className="align-middle mb-0"
                    size="sm" style={{ fontSize : "0.85rem" }}
                  >
                    <thead className="text-warning sticky-top ">
                      <tr>
                        <th width="40">
                          <Form.Check
                            type="checkbox"
                            checked={campaigns.every((c) => c.selected)}
                            onChange={(e) =>
                              e.target.checked ? selectAllCampaigns() : deselectAllCampaigns()
                            }
                          />
                        </th>
                        <th width="50">#</th>
                        <th width="250">Campaign Name</th>
                        <th width="120">Keyword</th>
                        <th width="80">Match</th>
                        <th width="80">Bid</th>
                        <th width="80">Budget</th>
                        <th width="120">Start Date</th>
                        <th width="120">End Date</th>
                        <th width="80">SV</th>
                        <th width="100">Group</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={campaign.selected || false}
                              onChange={() => toggleCampaignSelection(idx)}
                            />
                          </td>
                          <td>{idx + 1}</td>
                          <td>
                            <div
                              className="text-wrap text-break" style={{ maxWidth : "250px" }}
                            >
                              {campaign["Campaign Name"]}
                            </div>
                          </td>
                          <td className="text-info">{campaign.Keyword}</td>
                          <td>
                            <Badge bg="secondary">
                              {campaign["Match Type"]}
                            </Badge>
                          </td>
                          <td>${campaign.Bid}</td>
                          <td>${campaign.Budget}</td>
                          <td>
                            {formatDateCell(
                              campaign["Start Date"] || campaign["Campaign Start Date"] || campaign["Start date"],
                            )}
                          </td>
                          <td>
                            {formatDateCell(
                              campaign["End Date"] || campaign["Campaign End Date"] || campaign["End date"],
                            )}
                          </td>
                          <td>
                            {Number(campaign["Search Volume"] || 0).toLocaleString()}
                          </td>
                          <td>
                            <Badge bg="primary">
                              {campaign.Group || "Default"}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                <Card.Footer className=" border-top border-secondary d-flex justify-content-between p-3">
                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={handleReset}
                      className="fw-bold"
                    >
                      ← Cancel
                    </Button>
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div className="text-muted small">
                      Total: {campaigns.length} | Selected:{" "}
                      {campaigns.filter((c) => c.selected).length}
                    </div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleCreateInAmazon}
                      disabled={
                        loading ||
                        creationComplete ||
                        campaigns.filter((c) => c.selected).length === 0
                      }
                      style={{
                        fontWeight: "bold",
                        padding: "6px 20px",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-1"
                          />
                          Creating in Amazon...
                        </>
                      ) : creationComplete ? (
                        <>
                            <FaCheckCircle className="me-1" /> Campaigns Created
                        </>
                      ) : (
                                  <>Create Selected Campaigns</>
                      )}
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        )}

        {/* Requirements Section */}
        {!result && (
          <div className="requirements-section">
            <span className="requirements-title">Requirements - CSV Files</span>
            <div className="req-item">
              <strong>Keywords CSV:</strong> Columns 'Keyword', 'SV' (Search
              Volume), 'Sugg Bid', 'Match Type'.
            </div>
            <div className="req-item">
              <strong>ASINs CSV:</strong> Columns 'ASIN', 'SKU'. Optional:
              'Abbreviation'.
            </div>
            <div className="req-item">
              <strong>AI Query:</strong> Use natural language like "sv greater than
              30k" or "search volume between 10k and 50k".
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default SpKeywordGroup;



